export type VideoProject = {
  id: string
  path: string
  title: string
  video: string
  previewVideo?: string
  poster: string
  tags: string[]
  about: string
  team: { role: string; name: string; href?: string }[]
  impact: string
  lessons: string[]
}

const projects = [
  {
    id: 'netflix-streaks', title: 'Netflix Streaks', file: 'netflix-streaks-v11',
    tags: ['Concept', 'Product', 'Motion'],
    about: 'An imaginary Netflix case based on an unshipped retention redesign for a previous client. An earlier version shipped and hurt KPIs; this revision was not integrated. Netflix is the demonstration setting, not the client.',
    team: [
      { role: 'Feature & confetti', name: 'Gosha Knyazhev' },
      { role: 'Flame animation', name: 'aristote · Rive', href: 'https://www.rive.app/marketplace/27337-51650-dynamic-streak-fire/' },
      { role: 'Base interface', name: 'Jason Liu · adapted', href: 'https://github.com/jason-liu22/netflix-clone-react-typescript' }
    ],
    impact: 'I redesigned the streak and reward flow, built the Netflix prototype and created the branded confetti. The work explores retention, a fixed reward economy and recovery after a missed day. No performance improvement is claimed for this unshipped version.',
    lessons: [
      'Keep the reward economy stable as the streak grows.',
      'Let people recover from a missed day and restart without a worse reward.',
      'Celebrate progress without interrupting the main flow.'
    ]
  },
  {
    id: 'battle-pass', title: 'Battle Pass', file: 'battle-pass-v6',
    tags: ['Concept', 'Product', 'Motion'],
    about: 'A progression concept connecting XP, ranks and a shared chat. The demo follows a level-up from the progress bar to a rank notification, a shared message and a collection of rank badges. Presented as a design prototype.',
    team: [{ role: 'Design & prototype', name: 'Gosha Knyazhev' }],
    impact: 'I connected the progress, rank and sharing states into one sequence. The rank badge carries the same identity from the notification into the chat and the badge collection, helping explain how the parts belong together.',
    lessons: [
      'Show the earned progress before asking someone to share it.',
      'Reuse the same visual object across transitions so the viewer can follow the change.',
      'Keep the rank readable while it moves. Decoration should not hide the achievement.'
    ]
  },
  {
    id: 'bbet', title: 'BBET', file: 'bbet-v12',
    tags: ['Concept', 'UX/UI', 'Motion'],
    about: 'A session-limit flow for a mobile casino. A warning prepares the player for a break; the dialog explains what remains available, and a persistent timer keeps the end time in view.',
    team: [
      { role: 'Feature design', name: 'Gosha Knyazhev' },
      { role: 'Base interface', name: 'BBets Redesign', href: 'https://www.figma.com/community/file/1394250767361171233/bbets-redesign' }
    ],
    impact: 'I reviewed competitor flows and designed the warnings, break dialog and countdown. I worked through alternative layouts, component states and motion to keep the change clear across the whole flow.',
    lessons: [
      'Put available actions first, so the player can see what to do next.',
      'Use a countdown for waiting and an exact end time for planning.',
      'Keep the timer visible after the dialog closes.'
    ]
  },
  {
    id: 'less-ms-search', title: 'Less ms Search', file: 'less-ms-search-v3',
    tags: ['Search', 'UX/UI', 'Prototype'],
    about: 'A game-search demo using natural-language phrases, loose descriptions and emoji. It shows the search state, matching results and a no-results recovery flow. The timing labels come from captured frontend measurements; the edited video is not a performance benchmark.',
    team: [{ role: 'Design & prototype', name: 'Gosha Knyazhev' }],
    impact: 'I brought query entry, search feedback and results into one readable flow. The empty state offers an emoji suggestion that leads back to results, giving the user a concrete next action when a query finds nothing.',
    lessons: [
      'Search should accommodate how people describe what they remember.',
      'Treat the empty state as part of search: offer a useful next query.',
      'Keep status changes readable and avoid overlapping old and new results.'
    ]
  },
  {
    id: 'united-custom-web', title: 'United Custom Web', file: 'united-custom-web-final',
    path: '/blog/united-accountants-custom-landing-pages-gtm-system',
    tags: ['AI', 'Web', 'Sales'],
    about: 'A personalized follow-up page workflow for United Accountants. Sales-call notes become a page built around one buyer’s concerns and the next step in the conversation. A shared structure keeps the pages consistent while the argument changes for each prospect.',
    team: [
      { role: 'Solution design', name: 'Gosha Knyazhev' },
      { role: 'GTM engineering', name: 'Aivars Bruno Salna' }
    ],
    impact: 'I designed the page structure and the process that turns call context into a focused follow-up. Reusable sections support different buyer concerns, while a human review checks the facts, message and next step before sharing.',
    lessons: [
      'Personalization starts with the buyer’s concern, not their name in a headline.',
      'Reuse the structure while adapting the argument to the conversation.',
      'Keep human review between an AI draft and a page sent to a prospect.'
    ]
  }
]

export const videoProjects: VideoProject[] = projects.map(({ file, ...project }) => ({
  ...project,
  path: project.path ?? `/design/${project.id}/`,
  video: `/design/videos/${file}.mp4`,
  previewVideo: `/design/videos/${file}.preview-v1.mp4`,
  poster: `/design/videos/${file}.jpg`,
}))
